import AvailableBalanceDelegate from '../delegates/AvailableBalance.delegate';
import TabController from './Tab.controller';
import Inherit from '../../../core/inherit';
import {
    DEFAULT_AVAILABLE_BALANCE_STATE,
    DEFAULT_BALANCE_ENABLED,
    PAYROLL_PREVIEW,
    BALANCE_RULE_TYPE,
    US_STATE_OPTIONS
} from '../configuration.constants';
import { clone } from '../../../utils/json';
import {
    getUserTenureOptions,
    getOverdraftOptions,
    getAchOptions,
    getBalanceOptions,
    sanitizeBalanceRules
} from '../utils/available-balance-options';
import {
    DEFAULT_RULE_TIMEFRAME,
    TIMEFRAME_RULE_TYPES
} from '../elements/availableBalanceForm/availableBalanceForm.constants';

/**
 * View-model for the "Available Balance" tab. Its render slice lives in
 * the host component's `state.tabs.availableBalance` (via
 * {@link TabController}); mutations route through the inherited delegate.
 * Segmentation-aware sanitization is triggered by the SegmentationController
 * when its own values change, passing the new values as explicit arguments
 * so the sanitization lands in the same React batch.
 */
class AvailableBalanceController extends Inherit(TabController, AvailableBalanceDelegate) {
    constructor(page) {
        super(page, 'availableBalance');
        this.snapshot = null;
    }

    /** Default render slice the host seeds `state.tabs.availableBalance` with. */
    getInitialState() {
        return clone(DEFAULT_AVAILABLE_BALANCE_STATE);
    }

    // --- Snapshot / change detection -----------------------------------

    captureSnapshot(source = this.state) {
        this.snapshot = {
            threshold: source.threshold,
            doubleDipCap: source.doubleDipCap,
            balance_rules: clone(source.balance_rules),
            balance_enabled: clone(source.balance_enabled)
        };
    }

    hasChanges() {
        if (!this.snapshot) return false;
        const { threshold, doubleDipCap, balance_rules, balance_enabled } = this.state;
        if (threshold !== this.snapshot.threshold) return true;
        if (doubleDipCap !== this.snapshot.doubleDipCap) return true;
        if (JSON.stringify(balance_rules) !== JSON.stringify(this.snapshot.balance_rules)) return true;
        if (JSON.stringify(balance_enabled) !== JSON.stringify(this.snapshot.balance_enabled)) return true;
        return false;
    }

    // --- Rule helpers ---------------------------------------------------

    addRule(ruleType, defaultCondition) {
        const stateKey = `${ruleType}Rules`;
        const currentRules = this.state.balance_rules[stateKey] || [];
        const newId = currentRules.length > 0 ? Math.max(...currentRules.map((r) => r.id)) + 1 : 1;

        let newRule = { id: newId, type: BALANCE_RULE_TYPE.EXACTLY, value: 50 };

        if (ruleType === 'employmentTenure') {
            // Employment tenure uses fromDays/toDays instead of condition
            newRule = { ...newRule, fromDays: 0, toDays: 30 };
        } else if (ruleType === 'productType') {
            newRule.productType = this.page.state.productTypeOptions[0]?.value ?? null;
        } else if (ruleType === 'geography') {
            newRule.states = [];
        } else {
            const condition = defaultCondition ?? this.resolveDefaultCondition(ruleType);
            if (!condition) return;
            newRule.condition = condition;
        }

        if (TIMEFRAME_RULE_TYPES.includes(ruleType)) {
            newRule.timeframeValue = DEFAULT_RULE_TIMEFRAME.timeframeValue;
            newRule.timeframeUnit = DEFAULT_RULE_TIMEFRAME.timeframeUnit;
        }
        this.appendRule(stateKey, ruleType, newRule);
    }

    removeRule(ruleType, ruleId) {
        this.removeBalanceRule(`${ruleType}Rules`, ruleId);
    }

    updateRule(ruleType, ruleId, field, value) {
        this.updateBalanceRule(`${ruleType}Rules`, ruleId, field, value);
    }

    toggleDoubleDipCap() {
        this.toggleBalanceRuleGroup('doubleDipCap');
    }

    resolveDefaultCondition(ruleType) {
        const segmentPolicy = this.page?.segmentation?.state?.segment_policy ?? {};
        const segmentationEnabled = this.page?.segmentation?.state?.segmentation_enabled ?? {};

        switch (ruleType) {
            case 'userTenure':
                return getUserTenureOptions(segmentPolicy, segmentationEnabled)[0]?.value || null;
            case 'overdraft':
                return getOverdraftOptions(segmentPolicy, segmentationEnabled)[0]?.value || null;
            case 'ach':
                return getAchOptions(segmentPolicy, segmentationEnabled)[0]?.value || null;
            case 'employmentTenure':
                return 'less-than-30';
            case 'balance':
                return getBalanceOptions(segmentPolicy, segmentationEnabled)[0]?.value || null;
            case 'employment':
                return 'full-time';
            default:
                return null;
        }
    }

    // --- Transforms -----------------------------------------------------

    transformRulesToSnakeCase(balanceRules = {}, balanceEnabled = this.state.balance_enabled) {
        const withDisabledFlag = (rules = [], enabled = true, isEmploymentTenure = false) =>
            (rules || []).map((rule) => {
                const { timeframeValue, timeframeUnit, fromDays, toDays, ...rest } = rule;
                const mapped = { ...rest, disabled: !enabled };
                if (timeframeValue !== undefined) mapped.timeframe_value = timeframeValue;
                if (timeframeUnit !== undefined) mapped.timeframe_unit = timeframeUnit;
                if (isEmploymentTenure) {
                    mapped.from_days = fromDays;
                    mapped.to_days = toDays;
                }
                return mapped;
            });

        return {
            user_tenure_rules: withDisabledFlag(balanceRules.userTenureRules, balanceEnabled?.userTenure ?? true),
            overdraft_rules: withDisabledFlag(balanceRules.overdraftRules, balanceEnabled?.overdraft ?? false),
            ach_rules: withDisabledFlag(balanceRules.achRules, balanceEnabled?.ach ?? false),
            employment_tenure_rules: withDisabledFlag(balanceRules.employmentTenureRules, balanceEnabled?.employmentTenure ?? false, true),
            employment_rules: withDisabledFlag(balanceRules.employmentRules, balanceEnabled?.employment ?? false),
            balance_rules: withDisabledFlag(balanceRules.balanceRules, balanceEnabled?.balance ?? false),
            product_type_rules: withDisabledFlag(balanceRules.productTypeRules, balanceEnabled?.productType ?? false),
            geography_rules: withDisabledFlag(balanceRules.geographyRules, balanceEnabled?.geography ?? false)
        };
    }

    transformRulesToCamelCase(balanceRules = {}) {
        const normalize = (rules = [], withTimeframe = false, isEmploymentTenure = false) =>
            rules.map((rule) => {
                // Detect legacy rules (have `deduction` but no `type`/`value`)
                // and upgrade them to the new structure with type 'exactly'.
                const isLegacy = rule.deduction !== undefined && rule.type === undefined;

                const normalized = {
                    id: rule.id,
                    type: rule.type ?? BALANCE_RULE_TYPE.EXACTLY,
                    value: rule.value ?? (isLegacy ? Number(rule.deduction ?? 50) : 50),
                    disabled: rule.disabled ?? false
                };

                if (isEmploymentTenure) {
                    normalized.fromDays = Number(rule.from_days ?? 0);
                    normalized.toDays = Number(rule.to_days ?? 30);
                } else if ('productType' in rule) {
                    normalized.productType = rule.productType;
                } else if ('states' in rule) {
                    normalized.states = rule.states ?? [];
                } else {
                    normalized.condition = rule.condition;
                }

                if (withTimeframe) {
                    normalized.timeframeValue = Number(
                        rule.timeframe_value ?? DEFAULT_RULE_TIMEFRAME.timeframeValue
                    );
                    normalized.timeframeUnit = rule.timeframe_unit ?? DEFAULT_RULE_TIMEFRAME.timeframeUnit;
                }
                return normalized;
            });

        const userTenureRules = normalize(balanceRules.user_tenure_rules || []);
        const overdraftRules = normalize(balanceRules.overdraft_rules || [], true);
        const achRules = normalize(balanceRules.ach_rules || [], true);
        const employmentTenureRules = normalize(balanceRules.employment_tenure_rules || [], false, true);
        const employmentRules = normalize(balanceRules.employment_rules || []);
        const balanceRuleList = normalize(balanceRules.balance_rules || []);
        const productTypeRules = normalize(balanceRules.product_type_rules || []);
        const geographyRules = normalize(balanceRules.geography_rules || []);

        const isGroupEnabled = (group) =>
            group.length === 0 ? false : !group.every((rule) => rule.disabled);

        const balanceEnabled = {
            userTenure: isGroupEnabled(userTenureRules),
            overdraft: isGroupEnabled(overdraftRules),
            ach: isGroupEnabled(achRules),
            employmentTenure: isGroupEnabled(employmentTenureRules),
            employment: isGroupEnabled(employmentRules),
            balance: isGroupEnabled(balanceRuleList),
            productType: isGroupEnabled(productTypeRules),
            geography: isGroupEnabled(geographyRules)
        };

        const stripDisabled = (rules) => rules.map(({ disabled, ...rest }) => rest);

        return {
            userTenureRules: stripDisabled(userTenureRules),
            overdraftRules: stripDisabled(overdraftRules),
            achRules: stripDisabled(achRules),
            employmentTenureRules: stripDisabled(employmentTenureRules),
            employmentRules: stripDisabled(employmentRules),
            balanceRules: stripDisabled(balanceRuleList),
            productTypeRules: stripDisabled(productTypeRules),
            geographyRules: stripDisabled(geographyRules),
            balanceEnabled
        };
    }

    // --- Lifecycle (orchestrator API) ----------------------------------

    /**
     * Pure: computes and returns this tab's initial slice from the FI
     * payload without touching React state. The segmentation slice is
     * passed in explicitly so balance rules can be sanitized against it
     * before the host commits everything in one batch. Seeds the snapshot.
     */
    applyFromFi(fiData, segmentationSlice = {}) {
        const next = this.getInitialState();

        if (fiData?.threshold !== undefined) {
            next.threshold = Number(fiData.threshold);
        }
        if (fiData?.double_dip_cap !== undefined) {
            next.doubleDipCap = Number(fiData.double_dip_cap);
        }

        if (fiData?.balance_rules) {
            const transformed = this.transformRulesToCamelCase(fiData.balance_rules);
            const { balanceEnabled, ...balanceOnly } = transformed;
            next.balance_rules = balanceOnly;
            next.balance_enabled = {
                userTenure: balanceEnabled?.userTenure ?? DEFAULT_BALANCE_ENABLED.userTenure,
                overdraft: balanceEnabled?.overdraft ?? DEFAULT_BALANCE_ENABLED.overdraft,
                ach: balanceEnabled?.ach ?? DEFAULT_BALANCE_ENABLED.ach,
                employmentTenure: balanceEnabled?.employmentTenure ?? DEFAULT_BALANCE_ENABLED.employmentTenure ?? false,
                employment: balanceEnabled?.employment ?? DEFAULT_BALANCE_ENABLED.employment,
                balance: balanceEnabled?.balance ?? DEFAULT_BALANCE_ENABLED.balance,
                productType: balanceEnabled?.productType ?? DEFAULT_BALANCE_ENABLED.productType,
                geography: balanceEnabled?.geography ?? DEFAULT_BALANCE_ENABLED.geography,
                doubleDipCap: next.balance_enabled?.doubleDipCap ?? DEFAULT_BALANCE_ENABLED.doubleDipCap
            };
        }

        next.balance_rules = sanitizeBalanceRules(
            next.balance_rules,
            segmentationSlice?.segment_policy ?? {},
            segmentationSlice?.segmentation_enabled ?? {}
        );

        this.captureSnapshot(next);
        return next;
    }

    /**
     * Returns the slice the orchestrator drops into the submit-all payload.
     */
    getValues() {
        const payload = {
            threshold: this.state.threshold,
            balance_rules: this.transformRulesToSnakeCase(
                this.state.balance_rules,
                this.state.balance_enabled
            )
        };
        if (this.state.doubleDipCap !== null && this.state.doubleDipCap !== undefined) {
            payload.double_dip_cap = this.state.doubleDipCap;
        }
        return payload;
    }

    // --- Submit / cancel -----------------------------------------------

    async handleSubmit() {
        if (!this.page.canModifyConfiguration()) {
            return;
        }
        try {
            this.page.setLoading(true);
            const aba = this.page.user?.fi?.aba || this.page.user?.access?.[0];
            if (!aba) {
                this.page.props.notificationApi.error({
                    message: 'Error',
                    description: 'Unable to find FI ABA'
                });
                return;
            }

            const response = await this.page.data.updateFI(aba, { fi_info: this.getValues() });

            if (response) {
                this.captureSnapshot();
                this.page.props.notificationApi.success({
                    message: 'Success',
                    description: 'Available balance configuration updated successfully'
                });
            }
        } catch (error) {
            console.error('Error updating balance:', error);
            this.page.props.notificationApi.error({
                message: 'Update Failed',
                description: 'Failed to update available balance configuration'
            });
        } finally {
            this.page.setLoading(false);
        }
    }

    handleCancel() {
        if (!this.snapshot) return;
        this.replaceState({
            threshold: this.snapshot.threshold,
            doubleDipCap: this.snapshot.doubleDipCap,
            balance_rules: clone(this.snapshot.balance_rules),
            balance_enabled: clone(this.snapshot.balance_enabled)
        });
    }

    // --- Derived data ---------------------------------------------------

    getSummary() {
        const { threshold, doubleDipCap, balance_rules, balance_enabled } = this.state;
        const {
            userTenureRules,
            overdraftRules,
            achRules,
            employmentRules,
            balanceRules
        } = balance_rules || {};

        const { grossPay, payrollDeductions } = PAYROLL_PREVIEW;
        const netPay = grossPay - payrollDeductions;
        const baseThreshold = (threshold ?? 0);

        // Get minimum exactly value from a category's rules (if any match).
        const resolveCategory = (rules = [], enabled = false) => {
            if (!enabled || !rules.length) {
                return null; // Category disabled or empty: no adjustment.
            }

            const exactlyRules = rules.filter(r => r.type === BALANCE_RULE_TYPE.EXACTLY);
            if (exactlyRules.length === 0) return null;

            const minExactly = Math.min(...exactlyRules.map(r => Number(r.value) || 100));
            return minExactly / 100; // Convert percentage to decimal.
        };

        const userTenureValue = resolveCategory(userTenureRules, balance_enabled?.userTenure);
        const overdraftValue = resolveCategory(overdraftRules, balance_enabled?.overdraft);
        const achValue = resolveCategory(achRules, balance_enabled?.ach);
        const employmentValue = resolveCategory(employmentRules, balance_enabled?.employment);
        const balanceValue = resolveCategory(balanceRules, balance_enabled?.balance);

        // Final threshold is the minimum across all matched rules (most conservative).
        const matchedValues = [userTenureValue, overdraftValue, achValue, employmentValue, balanceValue]
            .filter(v => v !== null);

        const finalThreshold = matchedValues.length > 0 ? Math.min(...matchedValues) : baseThreshold;
        const availableBalance = netPay * finalThreshold;

        return {
            grossPay,
            payrollDeductions,
            netPay,
            threshold: baseThreshold * 100,
            doubleDipCap: (doubleDipCap ?? 0) * 100,
            userTenureValue: (userTenureValue ?? baseThreshold) * 100,
            overdraftValue: (overdraftValue ?? baseThreshold) * 100,
            achValue: (achValue ?? baseThreshold) * 100,
            employmentValue: (employmentValue ?? baseThreshold) * 100,
            balanceValue: (balanceValue ?? baseThreshold) * 100,
            finalThreshold: finalThreshold * 100,
            availableBalance
        };
    }

    // --- View props -----------------------------------------------------

    getViewProps() {
        const { threshold, doubleDipCap, balance_rules, balance_enabled } = this.state;
        const segment_policy = this.page?.segmentation?.state?.segment_policy ?? {};
        const segmentation_enabled = this.page?.segmentation?.state?.segmentation_enabled ?? {};
        const { firstTimeSetup, loading, canModify, productTypeOptions } = this.page.state;

        const sanitized = sanitizeBalanceRules(balance_rules, segment_policy, segmentation_enabled);
        const productTypeRules = sanitized?.productTypeRules ?? [];
        const geographyRules = sanitized?.geographyRules ?? [];

        const thresholdDisplay =
            threshold !== null && threshold !== undefined ? Math.round(threshold * 100) : undefined;
        const doubleDipDisplay =
            doubleDipCap !== null && doubleDipCap !== undefined ? Math.round(doubleDipCap * 100) : undefined;

        return {
            values: { threshold: thresholdDisplay, doubleDipCap: doubleDipDisplay },
            rules: {
                userTenureRules: sanitized?.userTenureRules ?? [],
                overdraftRules: sanitized?.overdraftRules ?? [],
                achRules: sanitized?.achRules ?? [],
                employmentTenureRules: sanitized?.employmentTenureRules ?? [],
                employmentRules: sanitized?.employmentRules ?? [],
                balanceRules: sanitized?.balanceRules ?? [],
                productTypeRules,
                geographyRules
            },
            options: {
                userTenureOptions: getUserTenureOptions(segment_policy, segmentation_enabled),
                overdraftOptions: getOverdraftOptions(segment_policy, segmentation_enabled),
                achOptions: getAchOptions(segment_policy, segmentation_enabled),
                balanceOptions: getBalanceOptions(segment_policy, segmentation_enabled),
                productTypes: productTypeOptions,
                states: US_STATE_OPTIONS
            },
            enabled: balance_enabled,
            handlers: {
                setThreshold: (v) => this.setThreshold(v),
                setDoubleDipCap: (v) => this.setDoubleDipCap(v),
                toggleDoubleDipCap: this.toggleDoubleDipCap.bind(this),
                addRule: this.addRule.bind(this),
                removeRule: this.removeRule.bind(this),
                updateRule: this.updateRule.bind(this)
            },
            summary: this.getSummary(),
            loading,
            readOnly: !canModify,
            disabledSave: !canModify || (!firstTimeSetup && !this.hasChanges()),
            submitText: firstTimeSetup ? 'Submit All' : 'Save',
            confirmBeforeSubmit: !firstTimeSetup,
            onSubmit: firstTimeSetup
                ? this.page.handleContinue.bind(this.page)
                : this.handleSubmit.bind(this),
            onCancel: firstTimeSetup
                ? this.page.handleCancel.bind(this.page)
                : this.handleCancel.bind(this)
        };
    }
}

export default AvailableBalanceController;
